import { InjectionToken, Provider } from '@angular/core';
import { CustomProductSearchPage, PageNumbers, ProductGridBanner } from '@catalog/const';
import { ActionReducer, ActionReducerMap, MetaReducer, combineReducers } from '@ngrx/store';
import {
  PRODUCT_FEATURE,
  Product,
  ProductActions,
  ProductSearchPage,
  StateUtils,
  StateWithProduct,
} from '@spartacus/core';
import { CatalogActions } from '../actions';
import { CATEGORY_DETAILS, CATEGORY_TAGS, CATEGORY_TAG_ITEM, CatalogState, StateWithCatalog } from '../catalog-state';
import { reducer as productListLoadingReducer } from './product-list-loading.reducer';

export type CatalogReducers = CatalogState;

export function getReducers(): ActionReducerMap<CatalogReducers> {
  return {
    productListLoading: productListLoadingReducer,
    categoryTags: combineReducers({
      items: StateUtils.entityLoaderReducer(CATEGORY_TAGS),
      activeItem: StateUtils.loaderReducer(CATEGORY_TAG_ITEM),
    }),
    categoryDetails: StateUtils.loaderReducer(CATEGORY_DETAILS),
  };
}

export const reducerToken: InjectionToken<ActionReducerMap<CatalogReducers>> = new InjectionToken<
  ActionReducerMap<CatalogReducers>
>('CatalogReducers');

export const reducerProvider: Provider = {
  provide: reducerToken,
  useFactory: getReducers,
};

export function loadProductsByCodesFactory(): MetaReducer<StateWithProduct> {
  const metaReducer =
    (reducer: ActionReducer<StateWithProduct>): ActionReducer<StateWithProduct> =>
    (
      state: StateWithProduct,
      action:
        | CatalogActions.LoadProductsByCodes
        | CatalogActions.LoadProductsByCodesFail
        | CatalogActions.LoadProductsByCodesSuccess,
    ): StateWithProduct => {
      if (action.type === CatalogActions.LOAD_PRODUCTS_BY_CODES) {
        let newState = { ...state };

        const { scope } = <CatalogActions.LoadProductsByCodes>action;
        for (const code of (<CatalogActions.LoadProductsByCodes>action).productCodes) {
          newState = reducer(newState, new ProductActions.LoadProduct(code, scope));
        }

        return newState;
      }

      if (action.type === CatalogActions.LOAD_PRODUCTS_BY_CODES_FAIL) {
        let newState = { ...state };
        const { scope } = <CatalogActions.LoadProductsByCodesFail>action;
        for (const code of (<CatalogActions.LoadProductsByCodesFail>action).productCodes) {
          newState = reducer(newState, new ProductActions.LoadProductFail(code, {}, scope));
        }

        return newState;
      }

      if (action.type === CatalogActions.LOAD_PRODUCTS_BY_CODES_SUCCESS) {
        let newState = { ...state };
        const { productCodes, products, scope } = <CatalogActions.LoadProductsByCodesSuccess>action;

        for (const code of productCodes) {
          const product = products.find(({ code: productCode }: Product) => String(productCode) === String(code));
          if (product) {
            newState = reducer(newState, new ProductActions.LoadProductSuccess(product, scope));
          } else {
            newState = reducer(newState, new ProductActions.LoadProductFail(code, {}, scope));
          }
        }

        return newState;
      }

      return reducer(state, action);
    };

  return metaReducer;
}

export function loadMoreProductsFactory(): MetaReducer<StateWithCatalog & StateWithProduct> {
  const metaReducer =
    (reducer: ActionReducer<StateWithCatalog & StateWithProduct>): ActionReducer<StateWithCatalog & StateWithProduct> =>
    (
      state: StateWithCatalog & StateWithProduct,
      action: CatalogActions.LoadMoreProductsSuccess,
    ): StateWithCatalog & StateWithProduct => {
      if (action.type === CatalogActions.LOAD_MORE_PRODUCTS_SUCCESS) {
        const prevResults = state[PRODUCT_FEATURE].search.results;
        const prevProducts = prevResults.products ?? [];
        const prevBanners = (<CustomProductSearchPage>state[PRODUCT_FEATURE].search.results).banners || [];
        const prevPage = prevResults.pagination?.currentPage ?? 0;
        const nextPage = action.payload.pagination?.currentPage ?? 0;
        const isReverseDirection = nextPage < prevPage;
        const nextProducts = action.payload.products ?? [];
        // избегаем дублирования баннеров
        const nextBanners = ((<CustomProductSearchPage>action.payload).banners || []).filter(
          (banner: ProductGridBanner) =>
            !prevBanners.find((prevBanner: ProductGridBanner) => prevBanner.code === banner.code),
        );
        const pageNumbers: PageNumbers = isReverseDirection
          ? { prev: nextPage, next: state.customCatalog.productListLoading.pageNumbers?.next || 0 }
          : { prev: state.customCatalog.productListLoading.pageNumbers?.prev || 0, next: nextPage };

        const products = isReverseDirection ? [...nextProducts, ...prevProducts] : [...prevProducts, ...nextProducts];
        const banners = isReverseDirection ? [...nextBanners, ...prevBanners] : [...prevBanners, ...nextBanners];

        return reducer(
          {
            ...state,
            customCatalog: {
              ...state.customCatalog,
              productListLoading: {
                ...state.customCatalog.productListLoading,
                loadingMore: false,
                pageNumbers,
              },
            },
          },
          new ProductActions.SearchProductsSuccess(<ProductSearchPage>(<unknown>{
            ...action.payload,
            products,
            banners,
          })),
        );
      }

      if (action.type === ProductActions.SEARCH_PRODUCTS_SUCCESS) {
        return reducer(
          {
            ...state,
            customCatalog: {
              ...state.customCatalog,
              productListLoading: {
                ...state.customCatalog.productListLoading,
                pageNumbers: {
                  prev: action.payload.pagination?.currentPage ?? 0,
                  next: action.payload.pagination?.currentPage ?? 0,
                },
              },
            },
          },
          action,
        );
      }

      if (action.type === CatalogActions.LOAD_MORE_PRODUCTS_RESET) {
        return reducer(
          {
            ...state,
            customCatalog: {
              ...state.customCatalog,
              productListLoading: {
                ...state.customCatalog.productListLoading,
                pageNumbers: {
                  prev: 0,
                  next: 0,
                },
              },
            },
          },
          action,
        );
      }

      return reducer(state, action);
    };

  return metaReducer;
}
